import React, { useEffect, useState } from "react";
import {
  GetAllDebatesAdmin,
  GetDebateAdmin,
  GetAllLists,
} from "../../api-requests/Get";
import { CreateDebateAdmin } from "../../api-requests/Create";
import {
  UpdateDebateAdmin,
  DeleteDebateAdmin,
} from "../../api-requests/Updates";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";
import PageLoading from "../../components/PageLoading";

interface Debate {
  id: string;
  title: string;
  description?: string;
  totalVotes: number;
  options?: DebateOption[];
  created_at?: string;
  createdAt?: string;
  optionsCount?: number;
}

interface DebateOption {
  id: string;
  option_text: string;
  votes: number;
  percentage: number;
}

interface Category {
  id: string;
  name: string;
  description?: string;
}

interface DebateForm {
  title: string;
  description: string;
  categoryId: string;
  options: string[];
}

const DebatesAdmin = () => {
  const [debates, setDebates] = useState<Debate[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDebate, setEditingDebate] = useState<Debate | null>(null);
  const [formData, setFormData] = useState<DebateForm>({
    title: "",
    description: "",
    categoryId: "",
    options: ["", ""],
  });

  const fetchDebates = async () => {
    try {
      const response = await GetAllDebatesAdmin(getBearerHeaders());
      console.log("Debates response:", response);
      setDebates(response.debates || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching debates:", error);
      setDebates([]);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesData = await GetAllLists(); // This actually gets categories
      console.log("Categories:", categoriesData);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchDebates();
    fetchCategories();
  }, []);

  const handleCreateDebate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validOptions = formData.options.filter((opt) => opt.trim() !== "");
      if (validOptions.length < 2) {
        alert("Please provide at least 2 options");
        return;
      }

      // Convert string options to objects with label property
      const formattedOptions = validOptions.map((option) => ({
        label: option,
      }));

      await CreateDebateAdmin(getBearerHeaders(), {
        title: formData.title,
        description: formData.description,
        categoryId: formData.categoryId,
        options: formattedOptions,
      });

      setFormData({
        title: "",
        description: "",
        categoryId: "",
        options: ["", ""],
      });
      setShowForm(false);
      fetchDebates();
    } catch (error) {
      console.error("Error creating debate:", error);
      alert("Error creating debate");
    }
  };

  const handleUpdateDebate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDebate) return;

    try {
      const validOptions = formData.options.filter((opt) => opt.trim() !== "");
      if (validOptions.length < 2) {
        alert("Please provide at least 2 options");
        return;
      }

      // Convert string options to objects with label property
      const formattedOptions = validOptions.map((option) => ({
        label: option,
      }));

      await UpdateDebateAdmin(
        editingDebate.id,
        {
          title: formData.title,
          description: formData.description,
          categoryId: formData.categoryId,
          options: formattedOptions,
        },
        getBearerHeaders(),
      );

      setFormData({
        title: "",
        description: "",
        categoryId: "",
        options: ["", ""],
      });
      setEditingDebate(null);
      fetchDebates();
    } catch (error) {
      console.error("Error updating debate:", error);
      alert("Error updating debate");
    }
  };

  const handleEditDebate = async (debate: Debate) => {
    try {
      const response = await GetDebateAdmin(debate.id, getBearerHeaders());
      console.log("Edit debate response:", response);
      const fullDebate = response.debate;

      setEditingDebate(fullDebate);
      setFormData({
        title: fullDebate.title,
        description: fullDebate.description || "",
        categoryId: fullDebate.categoryId || "",
        options: fullDebate.options?.map(
          (opt: DebateOption) => opt.option_text,
        ) || ["", ""],
      });
      setShowForm(true);
    } catch (error) {
      console.error("Error fetching debate details:", error);
    }
  };

  const handleDeleteDebate = async (debateId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this debate? This action cannot be undone.",
      )
    ) {
      try {
        await DeleteDebateAdmin(debateId, getBearerHeaders());
        fetchDebates();
      } catch (error) {
        console.error("Error deleting debate:", error);
        alert("Error deleting debate");
      }
    }
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, ""],
    });
  };

  const removeOption = (index: number) => {
    if (formData.options.length <= 2) return;
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      options: newOptions,
    });
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({
      ...formData,
      options: newOptions,
    });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      categoryId: "",
      options: ["", ""],
    });
    setEditingDebate(null);
    setShowForm(false);
  };

  if (loading) {
    return <PageLoading />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Debates Administration</h1>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium"
          >
            Create New Debate
          </button>
        </div>

        {showForm && (
          <div className="bg-gray-800 p-6 rounded-lg mb-8">
            <h2 className="text-xl font-bold mb-4">
              {editingDebate ? "Edit Debate" : "Create New Debate"}
            </h2>
            <form
              onSubmit={editingDebate ? handleUpdateDebate : handleCreateDebate}
            >
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600"
                  rows={3}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Category
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) =>
                    setFormData({ ...formData, categoryId: e.target.value })
                  }
                  className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Options
                </label>
                {formData.options.map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1 p-3 rounded-lg bg-gray-700 text-white border border-gray-600 mr-2"
                      required
                    />
                    {formData.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded-lg"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addOption}
                  className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm"
                >
                  Add Option
                </button>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-medium"
                >
                  {editingDebate ? "Update Debate" : "Create Debate"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-600 hover:bg-gray-700 px-6 py-2 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid gap-6">
          {debates.map((debate) => (
            <div key={debate.id} className="bg-gray-800 p-6 rounded-lg">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold mb-2">{debate.title}</h3>
                  {debate.description && (
                    <p className="text-gray-300 mb-2">{debate.description}</p>
                  )}
                  <p className="text-sm text-gray-400">
                    Total Votes: {debate.totalVotes} | Created:{" "}
                    {debate.created_at
                      ? new Date(debate.created_at).toLocaleDateString()
                      : debate.createdAt
                        ? new Date(debate.createdAt).toLocaleDateString()
                        : "Unknown"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditDebate(debate)}
                    className="bg-yellow-600 hover:bg-yellow-700 px-3 py-1 rounded text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteDebate(debate.id)}
                    className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {debate.options && debate.options.length > 0 ? (
                  debate.options.map((option) => (
                    <div
                      key={option.id}
                      className="flex items-center justify-between bg-gray-700 p-3 rounded"
                    >
                      <span>{option.option_text}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${option.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-300">
                          {option.votes} ({option.percentage}%)
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 text-sm">
                    Click "Edit" to view options
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DebatesAdmin;
